import { Box, makeStyles, Theme } from '@material-ui/core';
import { Children, CSSProperties, Fragment, ReactNode } from 'react';

type WbHeaderActionsProps = {
  children: ReactNode;
  separator?: ReactNode;
  containerStyle?: CSSProperties;
  variant?: 'default' | 'separator';
};

type Items = ReturnType<(typeof Children)['toArray']>;

const useStyles = makeStyles<Theme>(
  theme => ({
    root: {
      display: 'flex',
      gap: '16px',
      alignItems: 'center',
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        width: '100%',
        gap: '8px',
        display: 'grid',
        marginTop: '6px',
      },
    },
    rootSingleItem: {
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        gridTemplateColumns: '1fr',
      },
    },
    rootMultipleItems: {
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        gridTemplateColumns: '1fr 1fr',
      },
    },
    separator: {
      height: '20px',
      width: '1px',
      backgroundColor: theme.palette.softened.secondary,
      [theme.breakpoints.down(theme.breakpoints.values.sidebar_breakpoint)]: {
        display: 'none',
      },
    },
  }),
  { name: 'WbHeaderActions' },
);

const Separator = () => {
  const classes = useStyles();
  return <Box className={classes.separator} />;
};

function insertSeparators(items: Items, separator: ReactNode) {
  return items.reduce<Items>((acc, current, index) => {
    let res = acc;
    if (index < items.length - 1) {
      res = acc.concat(
        current,
        <Fragment key={`separator${index}`}>{separator}</Fragment>,
      );
    } else {
      res.push(current);
    }

    return res;
  }, []);
}

export const WbHeaderActions = ({
  children,
  separator = <Separator />,
  containerStyle,
  variant = 'default',
}: WbHeaderActionsProps) => {
  const classes = useStyles();

  const elements = Children.toArray(children);
  const separatedElements =
    variant === 'separator' ? insertSeparators(elements, separator) : elements;
  const isSingleItem = elements.length === 1;

  return (
    <Box
      className={`${classes.root} ${
        isSingleItem ? classes.rootSingleItem : classes.rootMultipleItems
      }`}
      style={containerStyle}
    >
      {separatedElements}
    </Box>
  );
};
